import React, { useState, useMemo } from "react";
import Icon from "../../components/Icon";
import cn from "classnames";
import { formatDateTime } from "../../untils/index";
import { TranslationObject } from "../../models/translationModal";
import { getTranslation } from "../../untils/index";

const CONTENT_PREVIEW_LENGTH = 200;

function stripHtml(html: string): string {
    if (typeof document === "undefined") {
        return html.replace(/<[^>]*>/g, "");
    }
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    return tmp.textContent ?? tmp.innerText ?? "";
}

interface PostItemProps {
    post: {
        id: number;
        date: string;
        provider: string;
        title: string;
        content: string;
        link: string;
    };
    isClickable?: boolean;
    paymentUrl?: string;
    /** Тот же сценарий, что у кнопки оформления подписки на странице постов */
    onSubscribeClick?: () => void;
    onCustomerRequestAccess?: () => void;
    className: string;
    entities?: TranslationObject;
    disable?: boolean;
}

const PostItem: React.FC<PostItemProps> = ({
    post,
    className,
    isClickable,
    paymentUrl,
    onSubscribeClick,
    onCustomerRequestAccess,
    entities,
    disable = false,
}) => {
    const t = (entities: any, code: string) => getTranslation(entities, code);

    const [isContentExpanded, setIsContentExpanded] = useState(false);

    const plainContent = useMemo(
        () => stripHtml(post.content || ""),
        [post.content]
    );

    const isLongContent = plainContent.length > CONTENT_PREVIEW_LENGTH;
    const previewContent = isLongContent
        ? plainContent.slice(0, CONTENT_PREVIEW_LENGTH).trim() + "..."
        : plainContent;

    const showExpandLabel = t(entities, "devi_posts_show_more");
    const collapseLabel = t(entities, "devi_posts_collapse");
    const showExpandButton = isLongContent;

    const handleExpandClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        setIsContentExpanded((prev) => !prev);
    };

    const handlePostClick = () => {
        if (isClickable) {
            const url = post.link?.trim();
            if (url) {
                window.open(url, "_blank", "noopener,noreferrer");
            }
            return;
        }
        if (onSubscribeClick) {
            onSubscribeClick();
            return;
        }
        if (disable) return;
        if (onCustomerRequestAccess) {
            onCustomerRequestAccess();
        } else if (paymentUrl) {
            window.location.href = paymentUrl;
        }
    };

    const postClickable =
        isClickable ||
        !!onSubscribeClick ||
        (!disable && (!!paymentUrl || !!onCustomerRequestAccess));

    return (
        <div
            className={cn(
                "post-container",
                className,
                isContentExpanded && "post-container--expanded"
            )}
            onClick={handlePostClick}
            style={{
                cursor: postClickable ? "pointer" : "default",
            }}
            role={postClickable ? "button" : undefined}
        >
            <div className="post-item__header">
                <Icon
                    type={post.provider}
                    className="post-item__social-icon"
                    width={28}
                    height={28}
                />
                <div className="container__date-time">
                    <span className="post-item__date post-item__date-mb">
                        {formatDateTime(post.date, "date")}
                    </span>
                    <span className="post-item__time post-item__time-mb">
                        {formatDateTime(post.date, "time")}
                    </span>
                </div>
            </div>

            <div className="post-item__content">
                <p className="post-item__title">
                    {post.title && post.title.trim() !== ""
                        ? post.title
                        : "This is title"}
                </p>
                <p
                    className={cn(
                        "post-item__description",
                        isContentExpanded && "post-item__description--full"
                    )}
                >
                    {isContentExpanded
                        ? post.content
                        : previewContent || post.content}
                    {showExpandButton && (
                        <>
                            {" "}
                            <span
                                role="button"
                                tabIndex={0}
                                className="post-item__show-more"
                                onClick={handleExpandClick}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        handleExpandClick(
                                            e as unknown as React.MouseEvent
                                        );
                                    }
                                }}
                            >
                                {isContentExpanded
                                    ? collapseLabel
                                    : showExpandLabel}
                            </span>
                        </>
                    )}
                </p>
            </div>

            <div className="post-item__footer">
                <a
                    href={isClickable ? post.link : paymentUrl || post.link}
                    target={isClickable ? "_blank" : undefined}
                    rel={isClickable ? "noopener noreferrer" : undefined}
                    className={cn(
                        "post-item__link",
                        disable && "post-item__link--disable"
                    )}
                    onClick={(e) => {
                        e.stopPropagation();
                        if (isClickable) {
                            return;
                        }
                        if (onSubscribeClick) {
                            e.preventDefault();
                            onSubscribeClick();
                            return;
                        }
                        if (disable) {
                            e.preventDefault();
                            return;
                        }
                        if (onCustomerRequestAccess) {
                            e.preventDefault();
                            onCustomerRequestAccess();
                        }
                    }}
                >
                    <span className="post-item__span">
                        {t(entities, "devi_posts_go")}
                    </span>
                    <Icon type="back" className="rotate-180" />
                </a>
                <div className="container__date-time">
                    <span className="post-item__date post-item__date-dt">
                        {formatDateTime(post.date, "date")}
                    </span>
                    <span className="post-item__time post-item__time-dt">
                        {formatDateTime(post.date, "time")}
                    </span>
                </div>
            </div>
        </div>
    );
};

export default PostItem;
